import React, { useState } from 'react';
import { IRouteOverview } from '../../models/RouteOverview';
import {
  Grid,
  GridColumn,
  GridColumnMenuFilter,
} from '@progress/kendo-react-grid';
import CommandCell from '../userAdmin/CommandCell';
import { faEdit, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { process } from '@progress/kendo-data-query';
import ColumnMenu from '../shared/ColumnMenu';
import { locale } from '../../common/localization/localizationService';
import { Checkbox } from 'primereact/checkbox';
import {
  ExcelExport,
  ExcelExportColumn,
} from '@progress/kendo-react-excel-export';
import RouteService from '../../services/routeService';
import { Spinner2 } from '../shared/spinner2';
import { Toastr } from '../../utils/Toastr';

export interface IRoutesOverviewTabGridProps {
  data: Array<IRouteOverview>;
  className?: string;
  editRoute: (dataItem: IRouteOverview) => void;
  excelRef?: React.MutableRefObject<ExcelExport | null>;
}

export default function RoutesOverviewTabGrid(
  props: IRoutesOverviewTabGridProps,
): JSX.Element {
  const [dataState, setDataState] = useState({
    sort: [{ dir: 'desc', field: 'lastChanged' }],
    take: 26,
    skip: 0,
  });

  const [excelData, setExcelData] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const routeStopsExcelRef = React.useRef<ExcelExport | null>(null);

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: any) => {
    return GridColumnMenuFilter.active(field, dataState.filter);
  };

  const dataStateChange = (event: any) => {
    setDataState(event.data);
  };

  const UpdateRouteCell = (columnProps: any) => {
    return (
      <>
        <CommandCell
          {...columnProps}
          edit={props.editRoute}
          icon={faEdit}
          tooltip={locale.routeOverview._editRoute}
        />
      </>
    );
  };

  const ExportRoteStopsCell = (columnProps: any) => {
    return (
      <>
        <CommandCell
          {...columnProps}
          edit={exportRouteStops}
          icon={faFileExcel}
          tooltip={locale.routeOverview._export}
        />
      </>
    );
  };

  const exportRouteStops = async (dataItem: any) => {
    setIsLoading(true);
    try {
      const routeLines =
        await RouteService.getRouteLinesWithRouteLineAgreements(
          dataItem.route,
        );
      exportToExcel(routeLines);
    } catch (err) {
      Toastr.error(err);
      setIsLoading(false);
    }
  };

  const exportToExcel = (routeLines: any[]) => {
    const excelData: any[] = [];

    try {
      routeLines.forEach((rl) => {
        rl.routelineAgreements.forEach((rla: any) => {
          const excelRow = {
            Route: rl.route,
            Sequence: rl.sequence,
            DegLat: rl.degLat,
            DegLong: rl.degLong,
            AgreementLineId: rla.agreementLineId,
            System: rla.paSystem,
          };
          excelData.push(excelRow);
        });
      });

      setExcelData(excelData);

      setTimeout(() => {
        routeStopsExcelRef.current?.save();
      });
    } catch (err) {
      Toastr.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const isRouteEnabled = (gridProps: any) => {
    return (
      <td className="route-active-cell">
        <Checkbox checked={gridProps.dataItem.enabled} disabled />
      </td>
    );
  };
  const gridData = props.data.map((route) => {
    route.lastChanged = new Date(route.lastChanged);
    return route;
  });
  const result = process(gridData, dataState);
  return (
    <>
      <ExcelExport
        ref={routeStopsExcelRef}
        data={excelData}
        fileName="RouteStops.xlsx"
      >
        <ExcelExportColumn field="Route" title={locale.routeAdminPage._route} />
        <ExcelExportColumn
          field="Sequence"
          title={locale.routeAdminPage._sequence}
        />
        <ExcelExportColumn
          field="DegLat"
          title={locale.routeAdminPage._degLat}
        />
        <ExcelExportColumn
          field="DegLong"
          title={locale.routeAdminPage._degLong}
        />
        <ExcelExportColumn
          field="AgreementLineId"
          title={locale.routeAdminPage._agreementLineId}
        />
        <ExcelExportColumn
          field="System"
          title={locale.routeOverview._system}
        />
      </ExcelExport>
      <Spinner2 show={isLoading} />
      <Grid
        {...dataState}
        className={`route-administration-tab-grid ${props.className}`}
        data={result}
        selectedField="selected"
        pageable
        resizable
        pageSize={26}
        onDataStateChange={dataStateChange}
        sortable
      >
        <GridColumn
          {...columnProps('name')}
          title={locale.routeOverview._name}
          sortable
          filter="text"
        />
        <GridColumn
          {...columnProps('route')}
          title={locale.routeOverview._routeId}
          sortable
          filter="text"
          width={90}
        />
        <GridColumn
          {...columnProps('activeRouteLines')}
          title={locale.routeOverview._activeRouteStops}
          sortable
          filter="numeric"
          width={150}
        />
        <GridColumn
          {...columnProps('container')}
          title={locale.routeOverview._active}
          sortable
          cell={isRouteEnabled}
          width={75}
        />
        <GridColumn
          {...columnProps('lastChanged')}
          title={locale.routeOverview._lastChanged}
          format={'{0:' + locale.general._fullDateWithTime + '}'}
          sortable
          width={150}
          filter="date"
        />
        <GridColumn width={'40px'} cell={ExportRoteStopsCell} />
        <GridColumn width={'40px'} cell={UpdateRouteCell} />
      </Grid>
    </>
  );
}
